<template>
  <div>
    <Filters
      :filters="scheduleFilters"
      filter-key="stabilitySchedule"
      :initial-data="filtersStatus.stabilitySchedule"
      :loading="filtersLoading"
    />

    <el-card>
      <el-row
        type="flex"
        align="middle"
        justify="space-between"
      >
        <el-col :span="6">
          <el-button-group>
            <el-button
              size="small"
              icon="el-icon-arrow-left"
              @click="prev"
            />
            <el-button
              size="small"
              @click="today"
            >
              {{ $t('stability.schedule.today') }}
            </el-button>
            <el-button
              size="small"
              icon="el-icon-arrow-right"
              @click="next"
            />
          </el-button-group>
        </el-col>

        <el-col
          :span="12"
          class="sc-controls-center"
        >
          <span class="sc-range-label">{{ rangeLabel }}</span>
        </el-col>

        <el-col
          :span="6"
          class="sc-controls-end"
        >
          <el-radio-group
            v-model="viewMode"
            size="small"
            @change="onViewModeChange"
          >
            <el-radio-button label="twoWeek">
              {{ $t('stability.schedule.twoWeek') }}
            </el-radio-button>
            <el-radio-button label="dayGridMonth">
              {{ $t('stability.schedule.month') }}
            </el-radio-button>
          </el-radio-group>
        </el-col>
      </el-row>
    </el-card>

    <el-row>
      <el-col v-loading="loading">
        <FullCalendar
          ref="calendar"
          :options="calendarOptions"
        />
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import FullCalendar from '@fullcalendar/vue';
  import dayGridPlugin from '@fullcalendar/daygrid';
  import moment from 'moment';
  import axios from 'axios';
  import { mapGetters, mapActions } from 'vuex';
  import Filters from '@/components/Filters.vue';
  import { stabilityScheduleFilters } from '@/config';
  import { stabilityApi } from '@/api';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';

  const STATUS_COLORS = {
    overdue: '#F56C6C',
    completed: '#67C23A',
    pending: '#909399',
  };

  export default {
    name: 'StabilitySchedule',

    components: { FullCalendar, Filters },

    mixins: [abortControllerMixin, filterLoadingMixin],

    data() {
      const { viewMode, calendarDate } = this.$route.query;
      return {
        scheduleFilters: stabilityScheduleFilters,
        viewMode: viewMode || 'twoWeek',
        loading: false,
        events: [],
        rangeLabel: '',
        restoredDate: calendarDate
          ? moment(calendarDate, 'YYYY-MM-DD').toDate()
          : null,
        calendarReady: false,
        skipNextRouteUpdate: false,
      };
    },

    computed: {
      ...mapGetters(['filtersStatus']),

      calendarOptions() {
        return {
          plugins: [dayGridPlugin],
          initialView: this.viewMode,
          initialDate: this.restoredDate || undefined,
          views: {
            twoWeek: { type: 'dayGrid', duration: { weeks: 2 } },
          },
          headerToolbar: false,
          events: this.events,
          eventDisplay: 'block',
          eventContent: this.renderEvent,
          eventClick: this.onEventClick,
          datesSet: this.onDatesSet,
          firstDay: 1,
          height: 'auto',
          locale: 'it',
        };
      },
    },

    created() {
      this.parseQueryStringStatus(this.$route.query);
    },

    beforeRouteUpdate(to, from, next) {
      if (this.skipNextRouteUpdate) {
        this.skipNextRouteUpdate = false;
        next();
        return;
      }
      this.parseQueryStringStatus(to.query);
      this.fetchSchedule();
      next();
    },

    methods: {
      ...mapActions(['parseQueryStringStatus']),

      // ─── Calendar navigation ──────────────────────────────────────────

      prev() {
        this.$refs.calendar.getApi().prev();
      },

      next() {
        this.$refs.calendar.getApi().next();
      },

      today() {
        this.$refs.calendar.getApi().today();
      },

      onViewModeChange(viewName) {
        this.$refs.calendar.getApi().changeView(viewName);
      },

      onDatesSet(dateInfo) {
        this.rangeLabel = this.formatRangeLabel(dateInfo.start, dateInfo.end);
        this.fetchSchedule();
        if (this.calendarReady) {
          this.persistCalendarState();
        } else {
          this.calendarReady = true;
        }
      },

      persistCalendarState() {
        const api = this.$refs.calendar?.getApi();
        if (!api) return;
        const calendarDate = moment(api.view.currentStart).format('YYYY-MM-DD');
        const {
          viewMode: qView,
          calendarDate: qDate,
          ...rest
        } = this.$route.query;
        if (qView === this.viewMode && qDate === calendarDate) return;
        this.skipNextRouteUpdate = true;
        this.$router
          .replace({
            query: { ...rest, viewMode: this.viewMode, calendarDate },
          })
          .catch(() => {});
      },

      formatRangeLabel(start, end) {
        const from = moment(start);
        const to = moment(end).subtract(1, 'day');
        if (from.isSame(to, 'month')) {
          return `${from.format('D')} – ${to.format('D MMMM YYYY')}`;
        }
        if (from.isSame(to, 'year')) {
          return `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
        }
        return `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`;
      },

      // ─── Data fetching ────────────────────────────────────────────────

      buildFilterParams() {
        const filterData = this.filtersStatus.stabilitySchedule || {};
        return Object.entries(filterData).reduce(
          (acc, [name, values]) =>
            values && values.length
              ? Object.assign(acc, { [name]: values[0].key })
              : acc,
          {}
        );
      },

      async fetchSchedule() {
        const api = this.$refs.calendar?.getApi();
        if (!api) return;

        const { activeStart, activeEnd } = api.view;
        const signal = this.createAbortController();
        this.loading = true;
        this.filtersLoading = true;

        try {
          const filters = this.buildFilterParams();
          const { data } = await stabilityApi.getSchedule(
            activeStart,
            activeEnd,
            filters,
            { signal }
          );
          const toLocalDay = (value) =>
            this.$options.filters.date(value, 'YYYY-MM-DD');
          this.events = data.map((event) => {
            const start = toLocalDay(event.fromDate);
            const end = moment(toLocalDay(event.toDate), 'YYYY-MM-DD')
              .add(1, 'day')
              .format('YYYY-MM-DD');
            const color =
              STATUS_COLORS[event.completionStatus] || STATUS_COLORS.pending;
            return {
              // The match, not the time point: with the 2NF schema one time
              // point under two conditions is two events, so timepointId is no
              // longer unique and FullCalendar would collapse them.
              id: `${event.matchId}`,
              start,
              end,
              allDay: true,
              backgroundColor: color,
              borderColor: color,
              extendedProps: event,
            };
          });
        } catch (error) {
          if (axios.isCancel(error)) return;
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('stability.schedule.loadError'),
          });
        } finally {
          this.loading = false;
          this.filtersLoading = false;
        }
      },

      // ─── Event rendering ──────────────────────────────────────────────

      renderEvent(arg) {
        const {
          customerName,
          groupingIds,
          token,
          temperature,
          humidity,
          matrix,
        } = arg.event.extendedProps;
        const customer = customerName || '—';
        const groupings = groupingIds?.length ? groupingIds.join(', ') : '—';
        const humidityPart = humidity != null ? ` ${humidity}%RH` : '';
        // matrix tells apart two conditions that share temperature and humidity
        const matrixPart = matrix ? ` / ${matrix}` : '';
        const label = `${customer} · ${groupings} · ${token} ${temperature}°C${humidityPart}${matrixPart}`;

        // A DOM node, not an HTML string: matrix is free text typed by the user
        // and must never be interpolated into markup.
        const el = document.createElement('span');
        el.className = 'sc-event-label';
        el.textContent = label;
        // The label is ellipsised, and matrix makes it longer: expose the full
        // text on hover so the condition stays readable.
        el.title = label;
        return { domNodes: [el] };
      },

      // ─── Click to navigate ────────────────────────────────────────────

      onEventClick(info) {
        const { groupingIds, accepted } = info.event.extendedProps;
        if (!groupingIds || !groupingIds.length) return;
        if (!accepted) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('stability.schedule.notAccepted'),
          });
          return;
        }
        this.$router.push({
          name: 'analysisStatusManagementIndex',
          query: {
            filterKey: 'analysesStatus',
            filterData: JSON.stringify({
              groupingId: groupingIds.map((id) => ({
                key: String(id),
                label: String(id),
              })),
            }),
          },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  :deep(.el-card) {
    background-color: $--color-black-lighter;
    border-color: $--color-black;
  }

  :deep(.fc) {
    --fc-button-bg-color: #{$--color-primary};
    --fc-button-border-color: #{$--color-primary};
    --fc-button-hover-bg-color: #{darken($--color-primary, 8%)};
    --fc-button-active-bg-color: #{darken($--color-primary, 8%)};
    --fc-event-border-color: transparent;
    --fc-border-color: #{$--border-color-base};
    --fc-page-bg-color: #1e2235;
    --fc-neutral-bg-color: #{$--color-text-secondary};
    --fc-today-bg-color: #{rgba($--color-primary, 0.08)};
    --fc-small-font-size: 0.8rem;
  }

  :deep(.fc-col-header-cell-cushion),
  :deep(.fc-daygrid-day-number) {
    color: #{$--color-text-regular};
  }

  :deep(.fc-event) {
    cursor: pointer;
  }

  :deep(.fc-daygrid-event) {
    border-radius: 0.25rem;
  }

  :deep(.sc-event-label) {
    display: block;
    width: 100%;
    font-size: 0.75rem;
    padding: 0 0.35rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #fff;
  }

  .sc-controls-center {
    text-align: center;
  }

  .sc-controls-end {
    text-align: right;
  }

  .sc-range-label {
    font-size: 1rem;
    font-weight: 600;
  }
</style>
